import { getEnvFromHostname, RuntimeEnvironment } from "@growtherapy/analytics";
import { Announcement, AppState, Browser, DeviceType } from "../../types";
import { checkIsAllowed, detectBrowser, getDeviceType } from "../../utils";
import { announcementConfig } from "../../statsig/gates";
import { useEffect, useMemo } from "react";
import { toast } from "../../toasts";
import { AnnouncementContent } from "./AnnouncementContent";
import { getAppConfig } from "../../config";
import { logger } from "../../datadog/logger";
import { useDynamicConfig } from "@statsig/react-bindings";
import { VideoPlatform } from "../../video-sdk/types";

export type CurrentState = {
  appState?: AppState;
  appVersion?: string;
  browser?: Browser;
  device?: DeviceType;
  environment?: RuntimeEnvironment;
  userId?: string;
  userType?: string;
  videoPlatform?: VideoPlatform | null;
};

export function checkShouldShowAnnouncement(
  {
    appState,
    appVersion,
    browser,
    device,
    environment,
    userId,
    userType,
    videoPlatform,
  }: CurrentState,
  announcement: Announcement,
) {
  const {
    allowAppStates,
    allowAppVersions,
    allowUserIds,
    browsers,
    content,
    denyAppStates,
    denyAppVersions,
    denyUserIds,
    devices,
    environments,
    expirationTimestamp,
    userTypes,
    videoPlatforms,
  } = announcement;
  return (
    !!content &&
    checkIsAllowed(appState, { allowList: allowAppStates }) &&
    checkIsAllowed(appState, { denyList: denyAppStates }) &&
    checkIsAllowed(appVersion, { allowList: allowAppVersions }) &&
    checkIsAllowed(appVersion, { denyList: denyAppVersions }) &&
    checkIsAllowed(browser, { allowList: browsers }) &&
    checkIsAllowed(device, { allowList: devices }) &&
    checkIsAllowed(environment, { allowList: environments }) &&
    checkIsAllowed(userId, { allowList: allowUserIds }) &&
    checkIsAllowed(userId, { denyList: denyUserIds }) &&
    checkIsAllowed(userType, { allowList: userTypes }) &&
    checkIsAllowed(videoPlatform, { allowList: videoPlatforms }) &&
    (!expirationTimestamp || Date.now() < expirationTimestamp)
  );
}

function showAnnouncementToasts(announcements: Announcement[]): string[] {
  return announcements.map((announcement) => {
    logger.info("Showing announcement banner", { announcement });
    return toast.info(
      <AnnouncementContent>{announcement.content}</AnnouncementContent>,
      { purpose: "system", timeout: Infinity },
    );
  });
}

/**
 * Hook to show important system announcements as toast banners. Please only use
 * this hook once per user experience e.g. once for client and once for
 * provider.
 */
export function useAnnouncementToast(givenCurrentState?: CurrentState) {
  const config = useDynamicConfig(announcementConfig);
  const announcements = (config?.value?.announcements ?? []) as Announcement[];
  const currentState = useMemo(
    () => ({
      appVersion: getAppConfig().version,
      browser: detectBrowser(),
      environment: getEnvFromHostname({ hostname: location.hostname }),
      device: getDeviceType(),
      ...givenCurrentState,
    }),
    [givenCurrentState],
  );
  const announcementsToShow = announcements.filter((announcement) =>
    checkShouldShowAnnouncement(currentState, announcement),
  );
  const shouldShowAnnouncement = !!announcementsToShow.length;

  useEffect(
    function handleAnnouncements() {
      if (!shouldShowAnnouncement) return;

      const shownToasts = showAnnouncementToasts(announcementsToShow);

      return function hideAnnouncements() {
        shownToasts.forEach((key) => toast.close(key));
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldShowAnnouncement],
  );
}
